import type { ConversationExecutionNode } from "@/features/chat/conversationSchemas";

const activeStatuses = new Set<ConversationExecutionNode["status"]>([
  "queued",
  "running",
  "waiting_input",
  "approval_required",
]);

const countedKinds = new Set<ConversationExecutionNode["kind"]>([
  "tool",
  "retrieval",
  "execution",
  "approval",
]);

const terminalStatuses = new Set<ConversationExecutionNode["status"]>([
  "succeeded",
  "failed",
  "canceled",
]);

export interface ConversationExecutionTreeNode {
  children: ReadonlyArray<ConversationExecutionTreeNode>;
  node: ConversationExecutionNode;
}

export interface ConversationExecutionTreeDiagnostic {
  code:
    | "cycle"
    | "depth_mismatch"
    | "duplicate_id"
    | "orphan"
    | "self_parent";
  nodeId: string;
}

export interface ConversationExecutionTree {
  activeNodeId: string | null;
  autoExpandedPath: ReadonlySet<string>;
  diagnostics: ReadonlyArray<ConversationExecutionTreeDiagnostic>;
  nodeById: ReadonlyMap<string, ConversationExecutionNode>;
  roots: ReadonlyArray<ConversationExecutionTreeNode>;
}

function compareNodes(
  left: ConversationExecutionNode,
  right: ConversationExecutionNode,
) {
  return left.sequence === right.sequence
    ? left.nodeId.localeCompare(right.nodeId)
    : left.sequence - right.sequence;
}

function isPreferredActiveNode(
  candidate: ConversationExecutionNode,
  current: ConversationExecutionNode,
) {
  if (candidate.depth !== current.depth) return candidate.depth > current.depth;
  if (candidate.sequence !== current.sequence) {
    return candidate.sequence > current.sequence;
  }
  if (candidate.revision !== current.revision) {
    return candidate.revision > current.revision;
  }
  return candidate.nodeId.localeCompare(current.nodeId) > 0;
}

export function countProcessedOperations(
  nodes: Iterable<ConversationExecutionNode>,
) {
  let count = 0;
  for (const node of nodes) {
    if (countedKinds.has(node.kind) && terminalStatuses.has(node.status)) {
      count += 1;
    }
  }
  return count;
}

export function completedOperationSummary(
  nodes: Iterable<ConversationExecutionNode>,
) {
  const all = [...nodes];
  const counted: ConversationExecutionNode[] = [];
  for (const node of all) {
    if (countedKinds.has(node.kind) && terminalStatuses.has(node.status)) {
      counted.push(node);
    }
  }
  if (counted.length === 0) {
    const root = all.find((node) => node.parentNodeId === null && node.kind === "reasoning_summary");
    if (root?.status === "succeeded") return "已回答";
    if (root?.status === "approval_required") return "需要确认后继续";
    if (root?.status === "waiting_input") return "等待补充信息";
    if (root?.status === "failed") return "回答未完成";
    if (root?.status === "canceled") return "已停止";
    return "尚未完成可确认的操作";
  }
  if (
    counted.every((node) => node.status === "succeeded" && isFreshPreviewReceipt(node))
  ) {
    if (counted.length === 1) {
      const receipt = counted[0]?.detailBlocks.find((block) =>
        block.type === "receipt" && block.label === "现采预览" && block.status === "not_started");
      if (receipt?.type === "receipt" && receipt.value) return receipt.value;
    }
    return "已读取计划/来源/请求上限，未启动采集";
  }
  if (
    counted.every((node) => node.status === "succeeded" && isScheduledTaskPreviewReceipt(node))
  ) {
    return "已核对定时任务计划，尚未创建任务";
  }
  if (
    counted.every((node) => node.status === "succeeded" && isMediaPrepareReceipt(node))
  ) {
    return "已准备、未提交生成";
  }
  return `已处理 ${counted.length} 个操作`;
}

function isFreshPreviewReceipt(node: ConversationExecutionNode) {
  return node.detailBlocks.some((block) =>
    block.type === "receipt" &&
    block.status === "not_started" &&
    block.label === "现采预览");
}

function isScheduledTaskPreviewReceipt(node: ConversationExecutionNode) {
  return node.detailBlocks.some((block) =>
    block.type === "receipt" &&
    block.status === "not_started" &&
    block.label === "定时任务预览");
}

function isMediaPrepareReceipt(node: ConversationExecutionNode) {
  return node.detailBlocks.some((block) =>
    block.type === "receipt" &&
    block.status === "not_started" &&
    block.label === "媒体准备");
}

export function buildConversationExecutionTree(
  nodes: ReadonlyArray<ConversationExecutionNode>,
): ConversationExecutionTree {
  const diagnostics: ConversationExecutionTreeDiagnostic[] = [];
  const nodeById = new Map<string, ConversationExecutionNode>();

  for (const node of nodes) {
    const current = nodeById.get(node.nodeId);
    if (current) {
      diagnostics.push({ code: "duplicate_id", nodeId: node.nodeId });
    }
    if (!current || current.revision <= node.revision) {
      nodeById.set(node.nodeId, node);
    }
  }

  const cycleNodes = findCycleNodes(nodeById);
  for (const nodeId of cycleNodes) {
    diagnostics.push({ code: "cycle", nodeId });
  }

  const childrenByParentId = new Map<string, ConversationExecutionNode[]>();
  const rootNodes: ConversationExecutionNode[] = [];

  for (const node of nodeById.values()) {
    const parentId = node.parentNodeId;
    if (parentId === null) {
      if (node.depth !== 0) {
        diagnostics.push({ code: "depth_mismatch", nodeId: node.nodeId });
      }
      rootNodes.push(node);
      continue;
    }
    if (parentId === node.nodeId) {
      diagnostics.push({ code: "self_parent", nodeId: node.nodeId });
      rootNodes.push(node);
      continue;
    }
    const parent = nodeById.get(parentId);
    if (!parent) {
      diagnostics.push({ code: "orphan", nodeId: node.nodeId });
      rootNodes.push(node);
      continue;
    }
    if (cycleNodes.has(node.nodeId) || cycleNodes.has(parentId)) {
      rootNodes.push(node);
      continue;
    }
    if (node.depth !== parent.depth + 1) {
      diagnostics.push({ code: "depth_mismatch", nodeId: node.nodeId });
      rootNodes.push(node);
      continue;
    }
    const children = childrenByParentId.get(parentId) ?? [];
    children.push(node);
    childrenByParentId.set(parentId, children);
  }

  for (const children of childrenByParentId.values()) {
    children.sort(compareNodes);
  }
  rootNodes.sort(compareNodes);

  let activeNode: ConversationExecutionNode | null = null;
  for (const node of nodeById.values()) {
    if (!activeStatuses.has(node.status)) continue;
    if (!activeNode || isPreferredActiveNode(node, activeNode)) {
      activeNode = node;
    }
  }

  const autoExpandedPath = new Set<string>();
  let cursor = activeNode;
  while (cursor && !autoExpandedPath.has(cursor.nodeId)) {
    autoExpandedPath.add(cursor.nodeId);
    cursor = cursor.parentNodeId
      ? (nodeById.get(cursor.parentNodeId) ?? null)
      : null;
  }

  const buildBranch = (
    node: ConversationExecutionNode,
  ): ConversationExecutionTreeNode => ({
    children: (childrenByParentId.get(node.nodeId) ?? []).map(buildBranch),
    node,
  });

  return {
    activeNodeId: activeNode?.nodeId ?? null,
    autoExpandedPath,
    diagnostics,
    nodeById,
    roots: rootNodes.map(buildBranch),
  };
}

function findCycleNodes(
  nodeById: ReadonlyMap<string, ConversationExecutionNode>,
) {
  const finished = new Set<string>();
  const cycleNodes = new Set<string>();

  for (const start of nodeById.values()) {
    if (finished.has(start.nodeId)) continue;
    const path: string[] = [];
    const pathIndex = new Map<string, number>();
    let cursor: ConversationExecutionNode | undefined = start;

    while (cursor && !finished.has(cursor.nodeId)) {
      const repeatedAt = pathIndex.get(cursor.nodeId);
      if (repeatedAt !== undefined) {
        for (let index = repeatedAt; index < path.length; index += 1) {
          const nodeId = path[index];
          if (nodeId) cycleNodes.add(nodeId);
        }
        break;
      }
      pathIndex.set(cursor.nodeId, path.length);
      path.push(cursor.nodeId);
      cursor = cursor.parentNodeId
        ? nodeById.get(cursor.parentNodeId)
        : undefined;
    }

    for (const nodeId of path) finished.add(nodeId);
  }

  return cycleNodes;
}

export function isExecutionNodeActive(node: ConversationExecutionNode) {
  return activeStatuses.has(node.status);
}
